import { Link } from "react-router-dom";
import {
  Users, Store, Package, ShoppingBag, CheckCircle2, Clock, MessageSquare, Truck,
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { useFetch, PageHeader, Loading, ErrorState, SectionCard, fmtDate, StatusChip } from "@/lib/ui";

const CARDS = [
  { key: "customers", label: "Customers", icon: Users, tone: "text-indigo-500", to: "/users" },
  { key: "shopkeepers", label: "Shopkeepers", icon: Store, tone: "text-amber-500", to: "/shops" },
  { key: "delivery_partners", label: "Delivery Partners", icon: Truck, tone: "text-emerald-500", to: "/users" },
  { key: "products", label: "Products", icon: Package, tone: "text-sky-500", to: "/catalog" },
  { key: "orders_total", label: "Total Orders", icon: ShoppingBag, tone: "text-violet-500", to: "/orders" },
  { key: "orders_delivered", label: "Delivered", icon: CheckCircle2, tone: "text-emerald-500", to: "/orders" },
  { key: "shops_pending", label: "Pending Approvals", icon: Clock, tone: "text-rose-500", to: "/shops" },
  { key: "open_tickets", label: "Open Tickets", icon: MessageSquare, tone: "text-orange-500", to: "/moderation" },
];

function aggregate(items) {
  const map = {};
  (items || []).forEach((o) => {
    const d = (o.created_at || "").slice(0, 10);
    if (!d) return;
    if (!map[d]) map[d] = { date: d, orders: 0, revenue: 0 };
    map[d].orders += 1;
    if (!["CANCELLED", "REFUNDED"].includes(o.status)) map[d].revenue += Number(o.pricing?.total || 0);
  });
  return Object.values(map)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(-14)
    .map((x) => ({ ...x, revenue: Math.round(x.revenue * 100) / 100, label: x.date.slice(5) }));
}

const AXIS = { fontSize: 11, fill: "hsl(var(--muted-foreground))" };

export default function Dashboard() {
  const { data, loading, error, refetch } = useFetch("/admin/dashboard", []);
  const chart = useFetch("/admin/orders?page=1&limit=100", []);
  const recent = useFetch("/admin/orders?page=1&limit=6", []);
  const series = aggregate(chart.data?.items);

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Marketplace overview — Sonu Ki Dukaan, Lucknow" />
      {loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4" data-testid="stats-grid">
            {CARDS.map((c) => (
              <Link key={c.key} to={c.to} className="group">
                <SectionCard className="p-4 hover:border-primary/40 transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">{c.label}</span>
                    <c.icon className={`h-4 w-4 ${c.tone}`} />
                  </div>
                  <div className="mt-2 text-2xl font-extrabold font-outfit tnum" data-testid={`stat-${c.key}`}>
                    {data?.[c.key] ?? 0}
                  </div>
                </SectionCard>
              </Link>
            ))}
          </div>

          <div className="grid gap-4 lg:grid-cols-2 mt-6">
            <SectionCard className="p-4" data-testid="revenue-chart">
              <h3 className="text-base font-semibold font-outfit mb-3">Revenue over time {series.length > 0 && <span className="text-xs font-normal text-muted-foreground">(last {series.length} days)</span>}</h3>
              {chart.loading ? <Loading /> : !series.length ? (
                <div className="h-[240px] flex items-center justify-center text-sm text-muted-foreground">No order data yet</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={false} />
                    <YAxis tick={AXIS} tickLine={false} axisLine={false} width={50} />
                    <Tooltip formatter={(v) => [`₹${v}`, "Revenue"]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
                    <Line type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </SectionCard>

            <SectionCard className="p-4" data-testid="orders-chart">
              <h3 className="text-base font-semibold font-outfit mb-3">Orders over time {series.length > 0 && <span className="text-xs font-normal text-muted-foreground">(last {series.length} days)</span>}</h3>
              {chart.loading ? <Loading /> : !series.length ? (
                <div className="h-[240px] flex items-center justify-center text-sm text-muted-foreground">No order data yet</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={false} />
                    <YAxis tick={AXIS} tickLine={false} axisLine={false} width={30} allowDecimals={false} />
                    <Tooltip formatter={(v) => [v, "Orders"]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
                    <Bar dataKey="orders" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </SectionCard>
          </div>

          <div className="mt-6">
            <h3 className="text-lg font-semibold font-outfit mb-3">Recent Orders</h3>
            <SectionCard className="overflow-hidden">
              {recent.loading ? <Loading /> : recent.error ? <ErrorState error={recent.error} onRetry={recent.refetch} /> :
                (recent.data?.items?.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                        <tr>
                          <th className="px-4 py-2.5 font-semibold">Order #</th>
                          <th className="px-4 py-2.5 font-semibold">Status</th>
                          <th className="px-4 py-2.5 font-semibold">Total</th>
                          <th className="px-4 py-2.5 font-semibold">Placed</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {recent.data.items.map((o) => (
                          <tr key={o.id} className="hover:bg-muted/30">
                            <td className="px-4 py-2.5 font-mono text-xs">{o.order_number}</td>
                            <td className="px-4 py-2.5"><StatusChip status={o.status} /></td>
                            <td className="px-4 py-2.5 tnum">₹{Number(o.pricing?.total || 0).toFixed(2)}</td>
                            <td className="px-4 py-2.5 text-muted-foreground text-xs">{fmtDate(o.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <div className="py-10 text-center text-sm text-muted-foreground">No orders yet</div>)}
            </SectionCard>
          </div>
        </>
      )}
    </div>
  );
}
